/**
 * Google Places API integration for HALO commute functionality
 */

import type { PlacePrediction, CommuteDestination } from './commuteTypes';

const POLL_INTERVAL_MS = 100;
const POLL_TIMEOUT_MS = 15_000;

/**
 * Initialize Google Maps services.
 *
 * Waits for `window.google.maps.places` to become available rather than
 * loading its own script.  DynamicMap (via gmapInit) handles the actual
 * script load and already requests `libraries=geometry,places`.  Loading
 * a second script with different params causes a double-init crash inside
 * the Google Maps API.
 */
export const initializeGoogleServices = async (_apiKey: string) => {
  if (window.google?.maps?.places) {
    return {
      autocompleteService: new window.google.maps.places.AutocompleteService(),
      geocoder: new window.google.maps.Geocoder(),
    };
  }

  return new Promise<{
    autocompleteService: google.maps.places.AutocompleteService;
    geocoder: google.maps.Geocoder;
  }>((resolve, reject) => {
    const start = Date.now();

    const poll = () => {
      if (window.google?.maps?.places) {
        resolve({
          autocompleteService: new window.google.maps.places.AutocompleteService(),
          geocoder: new window.google.maps.Geocoder(),
        });
        return;
      }

      if (Date.now() - start > POLL_TIMEOUT_MS) {
        reject(new Error('Google Maps Places library did not load in time'));
        return;
      }

      setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
  });
};

/**
 * Get place predictions from Google Places API
 */
export const getPlacePredictions = async (
  autocompleteService: google.maps.places.AutocompleteService,
  input: string,
): Promise<PlacePrediction[]> => {
  if (!input.trim()) {
    return [];
  }

  return new Promise((resolve, reject) => {
    autocompleteService.getPlacePredictions(
      {
        input: input.trim(),
        componentRestrictions: { country: 'us' },
        types: ['establishment', 'geocode'], // Include both places and addresses
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const formattedPredictions: PlacePrediction[] = predictions.map((prediction) => ({
            placeId: prediction.place_id,
            description: prediction.description,
            primaryText: prediction.structured_formatting.main_text,
            secondaryText: prediction.structured_formatting.secondary_text || '',
          }));
          resolve(formattedPredictions);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);
        } else {
          reject(new Error(`Places API error: ${status}`));
        }
      },
    );
  });
};

/**
 * Convert place ID to coordinates using Google Geocoder
 */
export const getPlaceDetails = async (
  geocoder: google.maps.Geocoder,
  placeId: string,
  description: string,
): Promise<CommuteDestination> => {
  return new Promise((resolve, reject) => {
    geocoder.geocode({ placeId }, (results, status) => {
      if (status === window.google.maps.GeocoderStatus.OK && results && results[0]) {
        const result = results[0];
        const location = result.geometry.location;

        resolve({
          placeId,
          name: result.formatted_address,
          address: description,
          lat: location.lat(),
          lng: location.lng(),
        });
      } else {
        reject(new Error(`Geocoding error: ${status}`));
      }
    });
  });
};
